// src/components/Indicadores/TextoPuntoDeEquilibrio/TextoPuntoDeEquilibrio.jsx

// ======================= IMPORTS BÁSICOS DE REACT Y REDUX =======================
// Hooks de React y utilidades de Redux para leer estado y despachar acciones.
import React, { useEffect, useMemo, useState, useRef, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// ======================= ACCIONES Y UTILIDADES DE FORMATEO =======================
// Acción para obtener datos del Punto de Equilibrio y utilidades de moneda/número.
import { obtenerPuntoDeEquilibrio } from '../../../../store/actions/puntoDeEquilibrio';
import { storageMonedaSigno } from '../../../../assets/shared/sessionData';
import { storageFechaFija } from "../../../../shared/sessionData";
import { formatNumber } from '../../../../assets/shared/formatNumber';

// ======================= UI (MUI) E ICONOGRAFÍA =======================
// Tooltips, botones e íconos neutros (sin semáforo de color).
import Tooltip from '@material-ui/core/Tooltip';
import { IconButton } from '@material-ui/core';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';
import FilterListIcon from '@material-ui/icons/FilterList';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

// ======================= COMPONENTES PROPIOS Y RECURSOS GRÁFICOS =======================
// Spinner de carga, barra de progreso y clases CSS del card de texto.
import Spinner from '../../../UI/Spinner/Spinner';
import BarraDeCarga from '../../../Encuestas/BarraDeCarga/BarraDeCarga';

import classes from '../../../Charts/Styles/TextChartCard.css';
import IndDeshabilitado from '../../../../assets/images/IndicadorDeshabilitado-Texto.png';
import SinResultado from '../../../../assets/images/SinResultados-Texto.png';

// Imágenes del “Señor Equilibrio” en distintas poses/estados.
import SeñorEquilibristaA from '../../../../assets/images/Equilibrio/SeñorEquilibristaA.png';
import SeñorEquilibristaA2 from '../../../../assets/images/Equilibrio/SeñorEquilibristaA2.png';
import SeñorEquilibristaB from '../../../../assets/images/Equilibrio/SeñorEquilibristaB.png';
import SeñorEquilibristaB2 from '../../../../assets/images/Equilibrio/SeñorEquilibristaB2.png';
import SeñorEquilibristaD from '../../../../assets/images/Equilibrio/SeñorEquilibristaD.png';

// Decorativos de la escena (hilo y sol).
import HiloEquilibrio from '../../../../assets/images/Equilibrio/HiloEquilibrio.png';
import Solcito from '../../../../assets/images/Equilibrio/Solcito.png';

// ======================= UTILIDADES LOCALES =======================
// Mapa para resolver un idMoneda a partir de su símbolo textual.
const SIMBOLO_A_ID = { $: 'PES', 'US$': 'DOL', '€': 'EUR' };
const getIdMoneda = ({ idMoneda, monedaSimbolo }) =>
  idMoneda || (monedaSimbolo && SIMBOLO_A_ID[monedaSimbolo]) || 'PES';

// Conversión segura a número (null si no es numérico).
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Lectura de sessionStorage como entero (primera clave válida).
const ssInt = (...keys) => {
  for (const k of keys) {
    const v = sessionStorage.getItem(k);
    if (v !== null && v !== undefined && v !== '') {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
};

// Lectura de sessionStorage como string (primera clave con valor).
const ssStr = (...keys) => {
  for (const k of keys) {
    const v = sessionStorage.getItem(k);
    if (v) return v;
  }
  return null;
};

// ======================= CONSTANTES DE POSICIONAMIENTO =======================
// Centro del pie en la imagen ORIGINAL (px desde el borde izquierdo)
const PX_CENTRO_PIE_ORIGINAL = 70;

// ======================= COMPONENTE PRINCIPAL =======================
// Mide la relación Ventas / Punto de Equilibrio, muestra barra de progreso y personaje animado.
const TextoPuntoDeEquilibrio = ({
  ejecucionDePagina,
  empresaId,
  idMoneda,
  monedaSimbolo,
  title,
  // props que quedan pero ya no afectan colores/bordes
  height = 160,
  disabled = false,
  border = false,
  comparacionInvertida = false,
  colorSpinner = 'black',
  imgSinDatos = false,
  onFiltersClick,
  onBodyClick,
  cursorPointer = false,
  // =================== PROP DE DEPURACIÓN ===================
  // Muestra el panel de overrides si no es producción.
  debugPanel = false,
  // =================== NUEVA PROP DE AYUDA ===================
  // Texto que explica qué muestra este componente (tooltip del ícono ?).
  helpText = 'Muestra cuánto de tu Punto de Equilibrio mensual ya fue cubierto por las ventas del mes.\n\nLa barra indica el avance y el personaje se posiciona según el porcentaje cumplido.\n\nLa barra se muestra azul si las ventas a la fecha igualan o superan las ventas promedio necesarias (a esta altura del mes) para llegar al Punto de Equilibrio. Si las ventas se encuentran por debajo de ese punto, la barra se muestra en rojo. Una vez alcanzado el Puento de Equilibrio, la barra se muestra en verde.'
}) => {
  // ========== REDUX ==========
  // dispatch para acciones y selector para tomar ventas MTD actuales (si existen).
  const dispatch = useDispatch();
  const ventasMTDRedux = useSelector((state) => state.ventas?.pieVentasComparativo?.mesActual);

  // ========== RESOLUCIÓN DE PARÁMETROS (empresa, año, mes, moneda) ==========
  // Empresa: prioridad props/ejecución; si no, sessionStorage; resultado numérico o null.
  const empresa = useMemo(() => {
    const val =
      ejecucionDePagina?.idEmpresa ??
      ejecucionDePagina?.empresaId ??
      empresaId ??
      ssInt('empresaId', 'idEmpresa', 'empresaSeleccionada', 'idEmpresaSeleccionada');
    return Number.isFinite(Number(val)) ? Number(val) : null;
  }, [ejecucionDePagina, empresaId]);

  // Moneda: prioriza idMoneda, si no deduce por símbolo; cae en 'PES' por defecto.
  const monedaId = useMemo(() => {
    const idMonedaSS = ssStr('idMoneda', 'monedaId');
    const simboloSS = ssStr('monedaSimbolo') || storageMonedaSigno?.();
    return getIdMoneda({
      idMoneda: ejecucionDePagina?.idMoneda ?? idMoneda ?? idMonedaSS,
      monedaSimbolo: ejecucionDePagina?.monedaSimbolo ?? monedaSimbolo ?? simboloSS
    });
  }, [ejecucionDePagina, idMoneda, monedaSimbolo]);

  // ========== ESTADOS Y CONSTANTES LOCALES PRINCIPALES ==========
  // loading: estado de carga; ventaMesPE: ventas del indicador; equilibrioMes: PE mensual.
  const [loading, setLoading] = useState(false);
  const [ventaMesPE, setVentaMesPE] = useState(0);
  const [equilibrioMes, setEquilibrioMes] = useState(0);
  const [fechaActual] = useState(() => new Date(storageFechaFija()));
  const mesActual = fechaActual.getMonth() + 1;
  const añoActual = fechaActual.getFullYear();

  // ======================= OVERRIDES (DEBUG) =======================
  // Permiten forzar valores para depuración sin tocar Redux ni el fetch.
  // Usar desde consola:
  //   window.setVentasForzadas(123456.78)  // o null para volver al valor real
  //   window.setPEForzado(200000)          // o null para volver al valor real
  const [ventasForzadas, setVentasForzadas] = useState(null); // número o null
  const [peForzado, setPEForzado] = useState(null);           // número o null

  // Exponer setters en window para facilitar pruebas rápidas.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.setVentasForzadas = setVentasForzadas;
      window.setPEForzado = setPEForzado;
    }
  }, []);

  // ========== EFECTO: FETCH DE PUNTO DE EQUILIBRIO ==========
  // Obtiene el histórico (12 meses) para el indicador 76 y extrae el mes solicitado.
  useEffect(() => {
    let cancel = false;
    const fetchData = async () => {
      setLoading(true);
      if (!empresa || !añoActual || !monedaId) {
        if (!cancel) setLoading(false);
        setVentaMesPE(0);
        setEquilibrioMes(0);
        return;
      }
      try {
        const data = await dispatch(
          obtenerPuntoDeEquilibrio({
            mes: 12,
            anio: añoActual,
            idMoneda: monedaId,
            idEmpresa: empresa,
            indicadorId: 76
          })
        );
        if (cancel) return;

        // Normalización de respuesta a un mapa por mes ("01".."12")
        const mapa = {};
        if (Array.isArray(data)) {
          data.forEach((it) => {
            const cod = String(it.mes ?? it.Mes ?? '').padStart(2, '0');
            mapa[cod] = {
              monto: toNum(it.monto ?? it.importe ?? it.total) ?? 0,
              eq: toNum(it.montoDeEquilibrio ?? it.montoEquilibrio) ?? 0
            };
          });
        }

        // Selección del registro del mes requerido
        const codReq = String(mesActual).padStart(2, '0');
        const reg = mapa[codReq] || { monto: 0, eq: 0 };

        setVentaMesPE(reg.monto);
        setEquilibrioMes(reg.eq);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    fetchData();
    return () => { cancel = true; };
  }, [dispatch, empresa, añoActual, mesActual, monedaId]);

  // ========== CÁLCULO: NUMERADOR (VENTAS MTD O INDICADOR) ==========
  // Prefiere el valor MTD de Redux; si no, usa el monto del indicador 76. Si hay override, lo aplica.
  const ventasNumerador = useMemo(() => {
    const v = Number(ventasMTDRedux);
    const base = Number.isFinite(v) ? v : ventaMesPE;
    return ventasForzadas ?? base;
  }, [ventasMTDRedux, ventaMesPE, ventasForzadas]);

  // ========== CÁLCULO: EQUILIBRIO EFECTIVO (REAL O FORZADO) ==========
  const equilibrioEfectivo = useMemo(() => {
    return peForzado ?? equilibrioMes;
  }, [peForzado, equilibrioMes]);

  // ========== CÁLCULO: PORCENTAJE VS PUNTO DE EQUILIBRIO ==========
  // Si no hay PE, pctNum = null; si hay, se redondea a 1 decimal.
  const pctNum = useMemo(() => {
    if (!equilibrioEfectivo) return null;
    const p = (ventasNumerador / equilibrioEfectivo) * 100;
    return Math.round((p + Number.EPSILON) * 10) / 10;
  }, [ventasNumerador, equilibrioEfectivo]);

  // ========== TEXTO PRINCIPAL Y BANDERAS DE UI ==========
  // Símbolo de moneda, texto grande (“Se cumplió el X%”) y flag para flecha/porcentaje.
  const simbolo = storageMonedaSigno?.() ?? '$';
  const bigDisplay = pctNum === null ? '—%' : `Se cumplió el ${pctNum.toLocaleString('es-AR')}%`;
  const showArrow = pctNum !== null;

  // ========== ESTADOS/VALORES DERIVADOS PARA ZONA IZQUIERDA ==========
  // Manejo de hover, comparador neutro (100%) y porcentaje mostrado (si corresponde).
  const [hover, setHover] = useState(false);

  const bigAmount = pctNum ?? 0;
  const smallAmount = showArrow ? 100 : undefined;
  const porcentaje = showArrow;

  // Diferencia porcentual (neutra), con redondeo a 2 decimales.
  let percIncrease = Math.round(((bigAmount / (smallAmount || 1) - 1) * 100 + Number.EPSILON) * 100) / 100;
  if (isNaN(percIncrease)) percIncrease = 0;

  // Clases CSS del contenedor.
  const cssClasses = [classes.TextChartCard];

  // Íconos neutros (gris) para indicar sube/baja/igual.
  const iconNeutralStyle = { color: '#6b7280' };
  const flechaUp = <TrendingUpIcon fontSize="large" style={iconNeutralStyle} />;
  const flechaDown = <TrendingDownIcon fontSize="large" style={iconNeutralStyle} />;
  const flechaFlat = <TrendingFlatIcon fontSize="large" style={iconNeutralStyle} />;

  // Lógica para mostrar el bloque de comparación.
  const mostrarTextoComparacion =
    porcentaje && smallAmount !== undefined && bigAmount !== undefined && smallAmount !== 0;

  let iconoSubidaBajada = null;
  let textoComparacion = null;
  if (mostrarTextoComparacion) {
    iconoSubidaBajada = percIncrease === 0 ? flechaFlat : percIncrease > 0 ? flechaUp : flechaDown;
    textoComparacion = `${Math.abs(percIncrease)}%`;
  }

  // ========== PORCENTAJE DE LA BARRA ==========
  // Estado de avance (malo/bueno/completo) y porcentaje normalizado a 0..100.
  const [estadoAvance, setEstadoAvance] = useState('malo');
  const porcentajeBarra = Math.max(0, Math.min(100, pctNum ?? 0));

  // ========== MODO EXTRA: VISIBILIDAD Y % MARGINAL (>100) ==========
  const pctMarginal = useMemo(() => {
    if (pctNum == null) return 0;
    const exceso = pctNum - 100;
    return exceso > 0 ? exceso : 0; // excedente por encima de 100
  }, [pctNum]);

  const mostrarBarraExtra = pctMarginal > 0; // sólo si supera 100%
  const porcentajeExtraValor = Math.round(pctMarginal); // ej: 123 -> 23

  // ========== SELECCIÓN DE IMAGEN DEL PERSONAJE ==========
  // Elige la pose del “Señor Equilibrio” según estadoAvance y porcentaje actual.
  const imgEquilibrio = useMemo(() => {

    //return SeñorEquilibristaB

    if ((estadoAvance === "malo") && (porcentajeBarra < 22)) {return SeñorEquilibristaA2};
    //if ((estadoAvance === "malo") && (porcentajeBarra > 74)) {return SeñorEquilibristaA2};
    if ((estadoAvance === "malo") && (porcentajeBarra > 21)) {return SeñorEquilibristaA};
    if ((estadoAvance === "bueno") && (porcentajeBarra < 15)) {return SeñorEquilibristaB2};
    //if ((estadoAvance === "bueno") && (porcentajeBarra > 74)) {return SeñorEquilibristaB2};
    if ((estadoAvance === "bueno") && (porcentajeBarra > 14)) {return SeñorEquilibristaB};
    if ((estadoAvance === "completo")) {return SeñorEquilibristaB};
    if ((estadoAvance === "sobresaliente")) {return SeñorEquilibristaD};
    return SeñorEquilibristaA2
  }, [estadoAvance, porcentajeBarra]);

  // ========== POSICIONAMIENTO DEL PERSONAJE (SINCRONIZADO CON LA BARRA) ==========
  // Refs del contenedor, la barra y la imagen; cálculo de “left” para que el pie coincida con el avance.
  const contRef = useRef(null);    // contenedor relativo (ÚNICO)
  const barWrapRef = useRef(null); // wrapper de la barra
  const imgRef = useRef(null);     // imagen del personaje

  const [leftPiePx, setLeftPiePx] = useState(0);

  // Recalcula la posición horizontal del personaje en función del % y tamaños renderizados.
  const recalcularLeft = () => {
    const cont = contRef.current;
    const barWrap = barWrapRef.current;
    const img = imgRef.current;
    if (!cont || !barWrap || !img) return;

    const contRect = cont.getBoundingClientRect();
    const barRect = barWrap.getBoundingClientRect();

    const naturalW = img.naturalWidth;
    const renderW = img.getBoundingClientRect().width;
    if (!naturalW || !renderW) return;

    // Ajuste del centro del pie según el escalado de la imagen renderizada.
    const centroPieEscalado = (PX_CENTRO_PIE_ORIGINAL / naturalW) * renderW;

    // Posición X dentro del contenedor en la que debería estar el pie (según % de barra).
    const barLeftDentroCont = barRect.left - contRect.left;
    const xLlenado = barLeftDentroCont + (porcentajeBarra / 100) * barRect.width;

    let left = xLlenado - centroPieEscalado;

    // Limites para no salirse de la barra visual.
    const minLeft = barLeftDentroCont - centroPieEscalado;
    const maxLeft = barLeftDentroCont + barRect.width - centroPieEscalado;
    if (left < minLeft) left = minLeft;
    if (left > maxLeft) left = maxLeft;

    // Offset actual conservado (-60).
    setLeftPiePx(left - 60);
  };

  // Recalcula en montaje, resize y cuando cambian porcentaje o imagen.
  useLayoutEffect(() => {
    let raf = 0;
    const run = () => { raf = requestAnimationFrame(recalcularLeft); };

    run();

    const onResize = () => run();
    window.addEventListener('resize', onResize);

    const ro = new ResizeObserver(run);
    if (barWrapRef.current) ro.observe(barWrapRef.current);
    if (imgRef.current) ro.observe(imgRef.current);

    return () => {
      window.removeEventListener('resize', onResize);
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [porcentajeBarra, imgEquilibrio]);

  // Decodifica la imagen (si el browser soporta) y luego recalcula la posición.
  const onImgLoad = async () => {
    const img = imgRef.current;
    if (!img) return;
    try { if ('decode' in img) await img.decode(); } catch {}
    recalcularLeft();
  };

  // ========== FECHAS DEL MES Y MÉTRICAS DERIVADAS ==========
  // Cálculo de “ventas esperadas a la fecha”, promedios y objetivo diario restante.
  const hoy = useMemo(() => new Date(), []);
  const diaActual = hoy.getDate(); // 1..31

  const diasMes = useMemo(() => {
    return new Date(añoActual, mesActual, 0).getDate();
  }, [añoActual, mesActual]);

  // Ventas esperadas al día de hoy (PE mensual prorrateado por día).
  const ventasEsperadas = useMemo(() => {
    if (!equilibrioEfectivo || !diasMes) return 0;
    return (equilibrioEfectivo / diasMes) * diaActual;
  }, [equilibrioEfectivo, diasMes, diaActual]);

  // Promedio diario real hasta hoy (ventas acumuladas / día del mes).
  const promedioDiario = useMemo(() => {
    return diaActual > 0 ? (ventasNumerador / diaActual) : 0;
  }, [ventasNumerador, diaActual]);

  // Promedio diario necesario para alcanzar el PE con los días restantes.
  const promedioDiarioDeseable = useMemo(() => {
    const diasRestantes = Math.max(0, diasMes - diaActual);
    const restanteParaPE = Math.max(0, equilibrioEfectivo - ventasNumerador);
    if (diasRestantes === 0) return 0;
    return restanteParaPE / diasRestantes;
  }, [equilibrioEfectivo, ventasNumerador, diasMes, diaActual]);

  /* === ESTADO DE AVANCE === */
  // Determina 'malo' / 'bueno' / 'completo' / 'sobresaliente' según rendimiento.
  useEffect(() => {
    let nuevo = 'malo';
    // Si supera el PE en más de 9%, pasa a "sobresaliente"
    if (equilibrioEfectivo > 0 && ventasNumerador >= equilibrioEfectivo * 1.09) {
      nuevo = 'sobresaliente';
    }
    // Si supera o iguala el PE, pero no por más de 9%
    else if (equilibrioEfectivo > 0 && ventasNumerador >= equilibrioEfectivo) {
      nuevo = 'completo';
    }
    // Si no alcanzó el PE pero va en buen ritmo diario
    else if (promedioDiario >= promedioDiarioDeseable) {
      nuevo = 'bueno';
    }
    setEstadoAvance(nuevo);
  }, [equilibrioEfectivo, ventasNumerador, promedioDiario, promedioDiarioDeseable]);

  // ======================= RENDER DE UI =======================
  // Card contenedor, título, estado (disabled/loading/sin datos) y layout principal.
  return (
    <div
      className={cssClasses.join(' ')}
      onMouseEnter={() => onBodyClick && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        height: 'auto', 
        minHeight: 423,
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 0,
        boxShadow: '0px 3px 6px #00000029',
        cursor: onBodyClick || cursorPointer ? 'pointer' : 'default',
        position: 'relative',
        userSelect: 'none',
        transition: 'box-shadow 0.2s ease-in-out',
        width: '101%',
        marginLeft: '-5px'
      }}
      onClick={() => onBodyClick && onBodyClick()}
    >
      {/* =================== ÍCONO (?) SUPERIOR DERECHO =================== */}
      <div
        style={{
          position: 'absolute',
          top: 6,
          right: debugPanel ? 300 : 6, // si está abierto el panel de debug, evitamos superposición
          zIndex: 60
        }}
        onClick={(e) => e.stopPropagation()} // no propagar para que no dispare onBodyClick
      >
        <Tooltip
           title={<div style={{ whiteSpace: 'pre-line' }}>{helpText}</div>}
           enterDelay={200}
         >
          <IconButton size="small" aria-label="Ayuda del indicador">
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {/* =================== CABECERA: TÍTULO + (OPCIONAL) FILTROS =================== */}
      <div className={classes.CardTitle}>
        <p style={{ fontWeight: 'bold', marginTop: 13, marginBottom: 5, color: '#111827' }}>
          {title ??
            `Relación Ventas/Punto de equilibrio (${String(fechaActual.getMonth() + 1).padStart(2, "0")}/${String(fechaActual.getFullYear()).slice(2)})`}
        </p>
        {onFiltersClick && !disabled && (
          <Tooltip title="Ver filtros del indicador">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onFiltersClick(); }}>
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {/* =================== ESTADOS: DESHABILITADO / CARGANDO / SIN DATOS / CONTENIDO =================== */}
      {disabled ? (
        <img
          alt="Indicador Inhabilitado"
          className={classes.IndDeshabilitado}
          src={IndDeshabilitado}
        />
      ) : loading ? (
        <Spinner color={colorSpinner} />
      ) : imgSinDatos ? (
        <img
          src={SinResultado}
          width="auto"
          alt="Indicador sin resultado"
          className={classes.IndDeshabilitado}
        />
      ) : (
        <div style={{ display: 'flex', flexFlow: 'row'}}>
          <div className={classes.textos} style={{ width: '100%' }}>
            {/* =================== TEXTO PRINCIPAL (PORCENTAJE LOGRADO) =================== */}
            <Tooltip style={{ textAlign: 'left', marginTop: 5 }} title="">
              <div
                style={{ fontWeight: 'bold', marginBottom: 5, marginTop: 0, color: '#111827' }}
                className={classes.TextoGrande}
              >
                {bigDisplay}
              </div>
            </Tooltip>

            {/* =================== ZONA RELATIVA: PERSONAJE + HILO + BARRA =================== */}
            <div style={{ width: 262.5 }}>
              <div style={{ position: 'relative', width: '100%', height: '180px', overflow: 'visible', display: 'flex', justifyContent: 'center' }}>
                {/* Contenedor relativo (ÚNICO con ref) */}
                <div
                  ref={contRef}
                  style={{ position: 'relative', width: '100%', height: 180, overflow: 'visible', isolation: 'isolate' }}
                >
                  {/* Hilo (fondo) */}
                  <img
                    src={HiloEquilibrio}
                    alt="Hilo Equilibrio"
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: 135,
                      objectFit: 'contain',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                    draggable={false}
                  />

                  {/* Solcito (fondo) */}
                  <img
                    src={Solcito}
                    alt="Solcito"
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: 15,
                      objectFit: 'contain',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                    draggable={false}
                  />

                  {/* Personaje (delante) */}
                  <img
                    ref={imgRef}
                    src={imgEquilibrio}
                    alt="Señor Equilibrio"
                    decoding="async"
                    onLoad={onImgLoad}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: leftPiePx,
                      height: 150,
                      objectFit: 'contain',
                      pointerEvents: 'none',
                      transition: 'left 180ms ease',
                      zIndex: 2
                    }}
                    draggable={false}
                  />
                </div>

                {/* Barra (la medimos con ref) */}
                      <div
                        ref={barWrapRef}
                        style={{
                          position: 'absolute',
                          left: 24,
                          right: 30,
                          top: 154,
                          bottom: 8,
                        }}
                      >
                        <div style={{ position: 'relative', height: '12px', overflow: 'visible' }}>
                          {/* Barra extra detrás (sólo si >100%) */}
                          {mostrarBarraExtra && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: 0,
                                transform: 'translateY(-50%) translateX(205px)',
                                zIndex: 0, // detrás
                              }}
                            >
                              <BarraDeCarga modoExtra={true} porcentajeExtra={porcentajeExtraValor} />
                            </div>
                          )}
                          {/* Barra principal arriba */}
                          <div style={{ position: 'relative', zIndex: 2 }}>
                            <BarraDeCarga porcentaje={porcentajeBarra} estadoAvance={estadoAvance} />
                          </div>
                        </div>
                      </div>
              </div>
            </div>
            <div style={{ width: 350}}>
              {/* =================== LÍNEA INFERIOR: MÉTRICAS DESGLOSADAS =================== */}
              <Tooltip title="Punto de equilibrio">
                <div
                  style={{ marginTop: '-2.5px', paddingRight: 10, color: '#374151' }}
                  className={classes.TextoChico}
                >
                  <b>Ventas a la fecha (día {diaActual}/{diasMes}):</b> {simbolo}{formatNumber(ventasNumerador)} <br />
                  <b>V. de P.E. proporcional a hoy:</b> {simbolo}{formatNumber(ventasEsperadas)}<br /><br />
                  <b>Punto de equilibrio:</b> {simbolo}{formatNumber(equilibrioEfectivo)}<br />
                  <b>Diferencia Ventas/P.E.:</b> {simbolo}{formatNumber(ventasNumerador - equilibrioEfectivo)}<br /><br />
                  <b>Promedio ventas diarias:</b> {simbolo}{formatNumber(promedioDiario)}<br />
                  <b>Prom. v. diarias necesarias:</b> {simbolo}{formatNumber(promedioDiarioDeseable)}<br />
                </div>
              </Tooltip>
            </div>

            {/* =================== (OPCIONAL) BLOQUE DE FLECHA + % DIF NEUTRO ===================
            {mostrarTextoComparacion && (
              <div
                className={classes.IconoDiferenciaOPorcentaje}
                style={{
                  color: '#6b7280',
                  position: 'absolute',
                  bottom: 8,
                  right: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                {iconoSubidaBajada}
                {textoComparacion}
              </div>
            )}*/}

          </div>
        </div>
      )}

      {/* =================== PANEL DE DEPURACIÓN (SOLO DEV + debugPanel) =================== */}
      {process.env.NODE_ENV !== 'production' && debugPanel && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            marginLeft: "150px",
            right: 8,
            width: 280,
            background: 'rgba(255,255,255,0.95)',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            borderRadius: 8,
            padding: 10,
            zIndex: 50,
            fontSize: 12
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ fontWeight: 700, marginBottom: 6, color: '#111827' }}>Depuración — Overrides</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6, alignItems: 'center' }}>
            {/* Ventas a la fecha */}
            <label style={{ color: '#374151' }}>Ventas a la fecha</label>
            <button
              type="button"
              onClick={() => setVentasForzadas(null)}
              style={{ fontSize: 11, padding: '2px 6px', cursor: 'pointer' }}
              title="Volver al valor real"
            >
              Limpiar
            </button>
            <input
              type="number"
              step="any"
              placeholder="Ej: 120000.50"
              value={ventasForzadas ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setVentasForzadas(v === '' ? null : Number(v));
              }}
              style={{ gridColumn: '1 / span 2', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6 }}
            />

            {/* Punto de Equilibrio */}
            <label style={{ color: '#374151', marginTop: 6 }}>Punto de Equilibrio</label>
            <button
              type="button"
              onClick={() => setPEForzado(null)}
              style={{ fontSize: 11, padding: '2px 6px', cursor: 'pointer', marginTop: 6 }}
              title="Volver al valor real"
            >
              Limpiar
            </button>
            <input
              type="number"
              step="any"
              placeholder="Ej: 200000"
              value={peForzado ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setPEForzado(v === '' ? null : Number(v));
              }}
              style={{ gridColumn: '1 / span 2', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6 }}
            />

            {/* Ayuda rápida */}
            <div style={{ gridColumn: '1 / span 2', marginTop: 8, color: '#6b7280' }}>
              Tip: también podés usar <code>window.setVentasForzadas(n)</code> y <code>window.setPEForzado(n)</code>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ======================= EXPORT DEL COMPONENTE =======================
// Exporta el card de “Relación Ventas / Punto de Equilibrio” para su uso en el dashboard.
export default TextoPuntoDeEquilibrio;
